import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DocumentPicker } from './DocumentPicker.js';

interface VersionRow {
  readonly versionId: string;
  readonly versionNumber: string;
  readonly versionType: string;
  readonly changeSummary: string | null;
  readonly isCurrent: boolean;
}

const VERSION_TYPES = ['MINOR', 'MAJOR', 'PATCH'];

export function M23VersionHistoryPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [documentId, setDocumentId] = useState('');
  const [rows, setRows] = useState<VersionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [versionType, setVersionType] = useState('MINOR');
  const [changeSummary, setChangeSummary] = useState('');

  function fetchVersions(id: string): void {
    setLoading(true);
    setError(null);
    setRows(null);
    apiRequest<{ data: VersionRow[] }>(`/v1/documents/${encodeURIComponent(id)}/versions`, { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  function onLoad(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setNotice(null);
    fetchVersions(id);
  }

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setError(null);
    setNotice(null);
    setBusy('create');
    try {
      await apiRequest(`/v1/documents/${encodeURIComponent(id)}/versions`, {
        method: 'POST',
        token,
        body: { versionType, changeSummary: changeSummary.trim() || undefined },
      });
      setNotice(tr('m23.versionHistory.msgCreated'));
      setChangeSummary('');
      fetchVersions(id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onRestore(versionNumber: string): Promise<void> {
    const id = documentId.trim();
    if (!id) return;
    setError(null);
    setNotice(null);
    setBusy(`restore:${versionNumber}`);
    try {
      await apiRequest(`/v1/documents/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionNumber)}/restore`, {
        method: 'POST',
        token,
      });
      setNotice(tr('m23.versionHistory.msgRestored', { version: versionNumber }));
      fetchVersions(id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <main aria-label="version-history">
      <h1>{tr('m23.versionHistory.title')}</h1>
      <form aria-label="version-load-form" onSubmit={onLoad}>
        <DocumentPicker label={tr('m23.versionHistory.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <button type="submit">{tr('m23.versionHistory.loadVersions')}</button>
      </form>
      {rows && (
        <form aria-label="version-create-form" onSubmit={onCreate}>
          <label htmlFor="versionType">{tr('m23.versionHistory.versionType')}</label>
          <select id="versionType" value={versionType} onChange={(e) => setVersionType(e.target.value)}>
            {VERSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label htmlFor="changeSummary">{tr('m23.versionHistory.changeSummary')}</label>
          <input id="changeSummary" value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} />
          <button type="submit" disabled={busy !== null}>{tr('m23.versionHistory.createVersion')}</button>
        </form>
      )}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {loading && <p>{tr('m23.versionHistory.loading')}</p>}
      {rows && (
        <table aria-label="versions-table">
          <thead><tr><th>{tr('m23.versionHistory.colVersion')}</th><th>{tr('m23.versionHistory.colType')}</th><th>{tr('m23.versionHistory.colChangeSummary')}</th><th>{tr('m23.versionHistory.colCurrent')}</th><th>{tr('m23.versionHistory.colAction')}</th></tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.versionId}>
                <td>{v.versionNumber}</td>
                <td>{v.versionType}</td>
                <td>{v.changeSummary ?? '—'}</td>
                <td>{v.isCurrent ? tr('m23.versionHistory.yes') : tr('m23.versionHistory.no')}</td>
                <td>
                  <button
                    type="button"
                    aria-label={`restore-${v.versionNumber}`}
                    disabled={busy !== null || v.isCurrent}
                    onClick={() => onRestore(v.versionNumber)}
                  >
                    {tr('m23.versionHistory.restore')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
